<script lang="ts">
  /**
   * Die Kopier-Abfrage: vier Fragen, bevor die Variante entsteht.
   *
   * ## Warum überhaupt eine Rückfrage
   *
   * Sie ist keine Bestätigung. Kopieren ist verlustfrei, und ein „Wirklich?"
   * davor wäre Reibung an der falschen Stelle (siehe `Reibung.svelte`: die
   * gehört vor das Endgültige). Gefragt wird, **was mitkommt** — und das ist
   * eine Frage, die man hinterher nicht mehr stellen kann: Der Erreicht-Stand
   * einer Kopie lässt sich zwar von Hand zurücksetzen, aber nicht an dreißig
   * Stationen, und die Belege müsste man einzeln neu hochladen.
   *
   * ## Die Form kommt von der Reibung, das Gewicht nicht
   *
   * Übernommen sind die Mechanik und nichts sonst: `role="dialog"` mit
   * `aria-modal`, Escape führt hinaus, der Abbrechen-Knopf trägt den
   * Anfangsfokus und steht links. Nicht übernommen ist alles, was die Reibung
   * schwer macht — kein roter Rahmen, kein abzutippender Name, kein gesperrter
   * Knopf. Hier ist nichts zu verlieren.
   *
   * Enter bestätigt, weil der Kasten ein `form` ist. Das ist billiger als ein
   * Tastenwächter und trifft nebenbei den Fall, den ein `keydown` auf dem
   * Fenster übersehen würde: Enter im angehakten Kästchen.
   *
   * ## Warum der Dialog bei einem Fehler offen bleibt
   *
   * Weil der wahrscheinlichste Fehler hier der 413 ist — das Kontingent reicht
   * für die Belege nicht. Wer den Dialog dafür zugeklappt bekäme, müsste ihn
   * neu öffnen und alle vier Antworten wiederholen, um eine davon zu ändern.
   * Der Servertext steht dabei **unverändert** da: Er nennt beide Mengen, und
   * eine eigene Formulierung wüsste sie nicht.
   */
  import { KOPIERFELDER, type Kopierwahl } from './kopieren';
  import Hinweis from '../gestaltung/muster/Hinweis.svelte';
  import { fokusfalle } from '../gestaltung/fokusfalle';

  interface Props {
    /** Der Name der Vorlage — er steht im Titel, damit man die richtige kopiert. */
    reise: string;
    wahl: Kopierwahl;
    laeuft?: boolean;
    /** Der Text vom Server, unverändert. Der Dialog bleibt dabei stehen. */
    fehler?: string | null;
    onAendern: (schluessel: keyof Kopierwahl, angehakt: boolean) => void;
    onabbrechen?: () => void;
    onkopieren?: () => void;
  }
  let {
    reise,
    wahl,
    laeuft = false,
    fehler = null,
    onAendern,
    onabbrechen,
    onkopieren
  }: Props = $props();
</script>

<svelte:window
  onkeydown={(e) => {
    if (e.key === 'Escape' && !laeuft) onabbrechen?.();
  }}
/>

<!-- `role="dialog"` sitzt am Rahmen und nicht am `form`: Ein Formular ist kein
     interaktives Element, und die Rolle darauf ist ein a11y-Verstoß (der
     Prüfer sagt es auch). Zwei Elemente statt einem sind der Preis dafür, dass
     Enter aus dem echten `submit` kommt und nicht aus einem Tastenwächter. -->
<div class="kopierwahl" role="dialog" aria-modal="true" aria-label="Reise kopieren" use:fokusfalle>
  <form
    onsubmit={(e) => {
      e.preventDefault();
      onkopieren?.();
    }}
  >
    <div class="oben">
      <h2>„{reise}“ kopieren</h2>
      <!-- Der Satz sagt, wofür die Fragen da sind. Ohne ihn liest sich die
           Liste wie eine Einstellungsseite, und man kreuzt an, ohne zu wissen,
           wonach gefragt wurde. -->
      <p class="wozu">
        Eine Kopie ist die Variante: dieselbe Reise ein zweites Mal, um darin
        etwas anderes zu probieren und beide weiterzuplanen. Was aus der Vorlage
        mitkommt, entscheidest du hier.
      </p>
    </div>

    <div class="felder">
      {#each KOPIERFELDER as feld (feld.schluessel)}
        <label class="haken">
          <input
            type="checkbox"
            checked={wahl[feld.schluessel]}
            disabled={laeuft}
            onchange={(e) => onAendern(feld.schluessel, e.currentTarget.checked)}
          />
          <span class="text">
            {feld.beschriftung}
            <!-- Der Halbsatz steht unter der Beschriftung und nicht dahinter:
                 Als Klammerzusatz bräche er um, und dann stünde die Erklärung
                 auf Höhe des nächsten Kästchens. -->
            <span class="warum">{feld.erklaerung}</span>
          </span>
        </label>
      {/each}
    </div>

    {#if fehler}
      <Hinweis art="ernst" titel="Kopieren fehlgeschlagen">
        {fehler}
      </Hinweis>
    {/if}

    <div class="knoepfe">
      <!-- Abbrechen links und mit dem Anfangsfokus: derselbe Grund wie an der
           Reibung — der Weg heraus darf nicht gesucht werden müssen. -->
      <!-- svelte-ignore a11y_autofocus -->
      <button type="button" autofocus disabled={laeuft} onclick={onabbrechen}>Abbrechen</button>
      <button type="submit" class="primary" disabled={laeuft}>
        {laeuft ? 'kopiert …' : 'Kopieren'}
      </button>
    </div>
  </form>
</div>

<style>
  .kopierwahl {
    max-width: 34rem;
    width: 100%;
    background: var(--surface-hoch);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: var(--schatten);
    padding: var(--raum-8);
  }

  /* Der Rahmen trägt die Rolle, das Formular den Aufbau — siehe der Kommentar
     am Markup. */
  form {
    display: grid;
    gap: var(--gruppe-eng);
  }

  .oben {
    display: grid;
    gap: var(--raum-3);
  }

  h2 {
    margin: 0;
    font-size: var(--fs-titel);
    font-weight: var(--gw-halb);
  }

  .wozu {
    margin: 0;
    font-size: var(--fs-text);
    color: var(--text-dim);
    text-wrap: pretty;
  }

  .felder {
    display: grid;
    gap: var(--raum-3);
  }

  .haken {
    display: flex;
    align-items: start;
    gap: var(--raum-4);
    padding: var(--raum-3) 0;
    /* 44 px, weil es ein Tippziel ist — dieselbe Regel wie an jeder anderen
       Zeile des Hauses. */
    min-height: var(--tippziel);
    cursor: pointer;
  }
  .haken input {
    flex: none;
    width: auto;
    /* Das Kästchen auf die erste Textzeile setzen, nicht auf die Mitte des
       zweizeiligen Blocks: Sonst sitzt es zwischen Beschriftung und Halbsatz
       und sieht aus, als gehörte es zu beiden. */
    margin: 0.2em 0 0;
    accent-color: var(--accent);
  }
  .text {
    display: grid;
    gap: 2px;
    font-size: var(--fs-text);
  }
  .warum {
    font-size: var(--fs-fein);
    color: var(--text-schwach);
    text-wrap: pretty;
  }

  .knoepfe {
    display: flex;
    gap: var(--raum-5);
    justify-content: flex-end;
    flex-wrap: wrap;
  }
  .knoepfe button {
    font: inherit;
    font-size: var(--fs-basis);
    padding: 8px 17px;
    border-radius: var(--radius);
    cursor: pointer;
  }
</style>
